import { Box, Flex, HStack, Stack, Text } from '@chakra-ui/react';
import { GetConversation } from '@/client/services/api/graphql/gql/graphql';

import AIAgentFeedbackGroup from '../feedback/AIAgentFeedbackGroup';
import { AskEddy } from '@/client/components/icons/ContinuIcons';
import ExchangeReferenceItem from './ExchangeReferenceItem';
import ExchangeRelatedReferences from './ExchangeRelatedReferences';
import ExpandToggleButton from './ExpandToggleButton';
import MarkdownText from '../MarkdownText';
import TextType from '@/client/components/animated/text/TextType';
import { bucketReferences } from './exchangeUtils';
import { forwardRef, useState } from 'react';
import { useAIAgentStore } from '../../AIAgentStore';
import { useTranslation } from 'react-i18next';

const CITED_REFERENCES_COLLAPSED_LIMIT = 3;

interface ExchangeListItemProps {
  exchange: GetConversation;
  isLatestExchange: boolean;
}

const ExchangeListItem = forwardRef<HTMLDivElement, ExchangeListItemProps>(
  ({ exchange, isLatestExchange }, ref) => {
    const { id, query, response, references } = exchange;
    const { t } = useTranslation();

    const {
      setCompletedTypingExchanges,
      setIsConversationActive,
      isConversationActive,
      completedTypingExchanges,
    } = useAIAgentStore();

    const [isCitedExpanded, setIsCitedExpanded] = useState(false);

    const isProcessingResponse = id === 'PROCESSING_RESPONSE';

    const handleTypingComplete = (exchangeId: string) => {
      setCompletedTypingExchanges((prev) => new Set(prev).add(exchangeId));
      setIsConversationActive(false);
    };

    const { cited: citedReferences, related: relatedReferences } = bucketReferences(references);

    const hasMoreCited = citedReferences.length > CITED_REFERENCES_COLLAPSED_LIMIT;
    // reason: cited list is intentionally uncapped (the answer's own sources); only the related list caps at EXPANDED_LIMIT.
    const visibleCitedReferences = isCitedExpanded
      ? citedReferences
      : citedReferences.slice(0, CITED_REFERENCES_COLLAPSED_LIMIT);
    const hiddenCitedCount = citedReferences.length - CITED_REFERENCES_COLLAPSED_LIMIT;

    return (
      <Stack ref={ref} key={id} spacing={4}>
        <Flex justifyContent="flex-end">
          <Box paddingX={4} paddingY={2} backgroundColor="neutral.1300" borderRadius="12px">
            <Text fontSize="14px" lineHeight="24px" fontWeight={500} color="white">
              {query}
            </Text>
          </Box>
        </Flex>

        {response && (
          <HStack
            spacing={2.5}
            justifyContent="flex-start"
            paddingX={4}
            paddingY={2}
            alignItems="flex-start"
            backgroundColor="neutral.50"
            borderRadius="12px"
            width="fit-content"
          >
            <AskEddy boxSize={5} />

            <Stack spacing={4}>
              {isProcessingResponse && (
                <Text
                  fontSize="14px"
                  lineHeight="20px"
                  fontWeight={500}
                  whiteSpace="pre-wrap"
                  width="fit-content"
                  color="baseBlack"
                  sx={{
                    background:
                      'linear-gradient(120deg,rgba(0, 0, 0, 0.8) 40%,rgba(255, 255, 255, 0.8) 50%,rgba(0, 0, 0, 0.8) 60%)',
                    backgroundSize: '200% 100%',
                    WebkitBackgroundClip: 'text',
                    backgroundClip: 'text',
                    display: 'inline-block',
                    animation: 'shine 4s linear infinite',
                    '@keyframes shine': {
                      '0%': {
                        backgroundPosition: '100% 0',
                      },
                      '100%': {
                        backgroundPosition: '-100% 0',
                      },
                    },
                  }}
                >
                  {response}
                </Text>
              )}

              {!isProcessingResponse && isLatestExchange && isConversationActive ? (
                <TextType
                  text={response}
                  cursorCharacter="|"
                  typingSpeed={15}
                  pauseDuration={1000}
                  deletingSpeed={15}
                  cursorBlinkDuration={0.8}
                  onComplete={() => handleTypingComplete(id)}
                  renderContent={(text) => <MarkdownText content={text} />}
                />
              ) : (
                !isProcessingResponse && <MarkdownText content={response} />
              )}

              {(citedReferences.length > 0 || relatedReferences.length > 0) &&
                (isProcessingResponse ||
                  completedTypingExchanges.has(id) ||
                  !isLatestExchange ||
                  !isConversationActive) && (
                  <Stack
                    spacing={4}
                    sx={{
                      animation: 'fadeIn 0.5s ease-in-out',
                      '@keyframes fadeIn': {
                        '0%': {
                          opacity: 0,
                          transform: 'translateY(10px)',
                        },
                        '100%': {
                          opacity: 1,
                          transform: 'translateY(0)',
                        },
                      },
                    }}
                  >
                    {citedReferences.length > 0 && (
                      <>
                        <Text fontSize="14px" fontWeight={500} color="baseBlack">
                          {t('aiAgent.responseSources')}
                        </Text>

                        {visibleCitedReferences.map((reference, index) => (
                          <ExchangeReferenceItem
                            // eslint-disable-next-line react/no-array-index-key -- cited refs may lack contentId and sourceType+createdAt can collide; index disambiguates and the slice is append-only (never reordered).
                            key={`${reference.sourceType}-${reference.createdAt}-${index}`}
                            reference={reference}
                            index={index}
                            totalCount={visibleCitedReferences.length}
                          />
                        ))}
                        {hasMoreCited && (
                          <ExpandToggleButton
                            label={
                              isCitedExpanded
                                ? t('aiAgent.citedSources.showLess')
                                : t('aiAgent.citedSources.more', { count: hiddenCitedCount })
                            }
                            onClick={() => setIsCitedExpanded((prev) => !prev)}
                          />
                        )}
                      </>
                    )}
                    {relatedReferences.length > 0 && (
                      <ExchangeRelatedReferences relatedReferences={relatedReferences} />
                    )}
                  </Stack>
                )}
            </Stack>
          </HStack>
        )}

        <AIAgentFeedbackGroup exchange={exchange} />
      </Stack>
    );
  },
);

ExchangeListItem.displayName = 'ExchangeListItem';

export default ExchangeListItem;
